/* eslint-disable react/prop-types */
// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from "react";
import { X, CheckCircle, Loader2 } from "lucide-react";

const OnboardDialog = ({ isOpen, onClose, onConfirm }) => {
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setNotes("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await onConfirm(notes);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        {/* HEADER */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
            <CheckCircle size={20} className="text-primary" />
            Onboard Caregiver
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-4">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
            <p className="text-sm text-emerald-800 dark:text-emerald-300 font-medium">
              This will mark the caregiver as{" "}
              <span className="font-bold">Onboarded</span> and activate their
              account for job assignments.
            </p>
          </div>

          <p className="text-sm text-gray-500 dark:text-dark-muted">
            The caregiver's service category and work preferences are already
            set in their profile.
          </p>

          {/* NOTES */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
              Onboarding Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isLoading}
              rows={3}
              className="w-full p-3 bg-accent dark:bg-dark-border border border-gray-200 dark:border-dark-border rounded-xl text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              placeholder="Add any notes about the onboarding decision..."
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="p-6 pt-2 flex gap-3 border-t border-gray-100 dark:border-dark-border">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 py-3 rounded-xl bg-accent dark:bg-dark-border border border-gray-200 dark:border-dark-border text-text dark:text-dark-text font-medium hover:bg-gray-200 dark:hover:bg-dark-border/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Onboarding...
              </>
            ) : (
              "Confirm Onboarding"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardDialog;
